<template>
  <div class="attachment-container">
    <!-- TODO: add animation if dragging the file here -->
    <div v-if="isDragging" class="drag-overlay"></div>
    <div
      class="attachment-field d-flex flex-column align-items-center justify-content-center gap-3"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
    >
      <div
        v-if="fileName"
        class="attachment-field__file-attached d-flex flex-column align-items-center justify-content-center gap-2"
      >
        <img src="@/assets/images/xls-icon.png" width="43" />
        <p>{{ fileName }}</p>
        <b-spinner v-if="isLoading" />
      </div>

      <div v-else class="attachment-field__empty-state">
        <p>{{ $t('global.importData.chooseFile') }}</p>
        <p>{{ $t('global.importData.maksFile2MB') }}</p>
      </div>

      <McButton
        :text="$t('global.importData.selectFile')"
        variant="secondary"
        :disabled="isLoading"
        @click="handleLoadFile"
      />

      <input
        ref="file-input-ref"
        type="file"
        :accept="fileAccepted.join(', ')"
        hidden
        @change="handleFileChanged"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'
import * as Import from '@/services/rest-api/lib/fleet-task-instant/import'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    selectedUserGroup: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:isDisableNextBtn', 'selectFile'],
  data() {
    return {
      isLoading: false,
      isDragging: false,

      fileName: '',
      fileSize: null,
      file: undefined,
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', ['importFile']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasUserGroupFeature() {
      const hasScope = this.hasAccessAll(['tms-user-group:read'])
      const isSuperUser = this.isSuperUser()
      
      return hasScope && isSuperUser
    },
    fileAccepted() {
      return [
        '.csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/wps-office.xlsx',
      ]
    },
  },
  methods: {
    async HandleProcessFile() {
      if (this.importFile === undefined) return

      this.$emit('update:isDisableNextBtn', true)

      const res = await Import.uploadFile({ file: this.importFile })
      if (res.errors) {
        this.$emit('update:isDisableNextBtn', true)
        return
      }

      this.redirectToProcessFile()
    },
    redirectToProcessFile() {
      this.$router.push({
        name: 'Fleet Task Instant Import Process',
      })
    },
    handleLoadFile() {
      if (this.isLoading) {
        this.$mcToast.warning(this.$t('global.loader.pleaseWait'))
        return
      }

      if (this.hasUserGroupFeature) {
        this.$emit('selectFile')
      } else {
        this.openFileDialog()
      }
    },
    openFileDialog() {
      this.$refs['file-input-ref'].click()
    },
    handleDragOver(event) {
      event.preventDefault()
      this.isDragging = true
    },
    handleDragLeave() {
      this.isDragging = false
    },
    handleDrop(event) {
      event.preventDefault()

      this.isLoading = true
      this.$store.dispatch('fleetTaskInstant/setImportFile', event.dataTransfer.files[0])

      this.isDragging = false
      this.uploadFile()
    },
    resetInputFile(event) {
      this.fileName = ''
      this.fileSize = null
      this.$store.dispatch('fleetTaskInstant/setImportFile', undefined)

      if (event) {
        event.target.value = ''
      }

      this.isLoading = false
    },
    handleFileChanged(event) {
      this.isLoading = true

      const [file] = event.target.files
      if (!file) {
        this.isLoading = false
        return
      }

      this.$store.dispatch('fleetTaskInstant/setImportFile', file)

      this.uploadFile(event)
    },
    async uploadFile(event = undefined) {
      if (!this.fileAccepted.includes(this.importFile.type)) {
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.importData.invalidFileFormat'))

        this.resetInputFile(event)
        return
      }

      let fileSize = this.importFile.size
      const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
      if (fileSize > MAX_FILE_SIZE) {
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.importData.exceedMaxSize'))

        this.resetInputFile(event)
        return
      }

      this.fileName = this.importFile.name

      fileSize = this.importFile.size / 1024 / 1024
      this.fileSize = fileSize.toFixed(2)

      this.$emit('update:isDisableNextBtn', false)
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.attachment-container {
  position: relative;
  width: 100%;
  height: 100%;

  .attachment-field {
    border: 2px dashed $mep-primary-100;
    border-radius: 4px;
    width: 100%;
    height: 100%;

    p {
      line-height: 20px;
      margin: 0;
      text-align: center;
    }

    &__empty-state {
      color: $mep-color-text-300;
    }

    &__file-attached {
      width: 75%;
      text-align: center;
      font-weight: 600;
    }

    &__progress-bar {
      width: 100%;
    }
  }
}
</style>
